import type { Cents, ContractTerm, Iso, QuoteLine } from "./index.js";
import type { AutotaskPeriod } from "./catalog-v2.js";

export interface QuoteTemplateLine {
  productId: string;
  qty: number;
}

export interface QuoteTemplate {
  id: string;
  dealerId: string;
  name: string;
  term: ContractTerm;
  lines: QuoteTemplateLine[];
  pdfNotes: string;
  createdBy: string;
  createdAt: Iso;
  updatedBy: string;
  updatedAt: Iso;
}

export interface QuoteTemplateInput {
  dealerId?: string;
  name: string;
  term: ContractTerm;
  lines: QuoteTemplateLine[];
  pdfNotes?: string;
}

export interface QuoteTemplateUpdateInput {
  dealerId?: string;
  name?: string;
  term?: ContractTerm;
  lines?: QuoteTemplateLine[];
  pdfNotes?: string;
  expectedUpdatedAt: Iso;
}

export interface UnquotableTemplateLine {
  productId: string;
  qty: number;
  name: string;
  productName: string;
  /** Why it can't be quoted now: the catalog's own reason (checkProductQuotableV2), e.g. "Bills yearly in Autotask". */
  reason: string;
  /** Today's list price, or null when the catalog no longer has the product. */
  listPrice: Cents | null;
  /** How Autotask bills it, so "yearly" reads "$276.00 a year". null when unknown or the product is gone. */
  period: AutotaskPeriod;
}

/** DELETE /api/quote-templates/:id: the version the caller read; 409 when someone saved since. */
export interface QuoteTemplateDeleteInput {
  expectedUpdatedAt: Iso;
}

export interface QuoteTemplateDetail extends QuoteTemplate {
  pricedLines: QuoteLine[];
  unquotableLines: UnquotableTemplateLine[];
}

export function normalizeTemplateName(name: string): string {
  return name.trim().replace(/\s+/g, " ").toLowerCase();
}

export function validateTemplateInput(
  input: unknown,
): { ok: true; data: QuoteTemplateInput } | { ok: false; error: string } {
  if (!input || typeof input !== "object") {
    return { ok: false, error: "Invalid request body." };
  }
  const obj = input as Record<string, unknown>;
  if (typeof obj.name !== "string" || !obj.name.trim()) {
    return { ok: false, error: "Template name is required." };
  }
  if (
    !obj.term ||
    !["month_to_month", 12, 24, 36, 60].includes(obj.term as ContractTerm)
  ) {
    return {
      ok: false,
      error: "Valid contract term is required (month_to_month, 12, 24, 36, 60).",
    };
  }
  if (!Array.isArray(obj.lines)) {
    return { ok: false, error: "Lines array is required." };
  }
  for (const line of obj.lines) {
    if (!line || typeof line !== "object") {
      return {
        ok: false,
        error: "Each line must be an object with productId and qty.",
      };
    }
    const l = line as Record<string, unknown>;
    if (typeof l.productId !== "string" || !l.productId) {
      return { ok: false, error: "Each line requires a productId." };
    }
    if (typeof l.qty !== "number" || l.qty <= 0) {
      return { ok: false, error: "Line quantity must be greater than zero." };
    }
  }
  return {
    ok: true,
    data: {
      dealerId: typeof obj.dealerId === "string" ? obj.dealerId : undefined,
      name: obj.name.trim(),
      term: obj.term as ContractTerm,
      lines: obj.lines.map((l: { productId: string; qty: number }) => ({
        productId: l.productId,
        qty: l.qty,
      })),
      pdfNotes: typeof obj.pdfNotes === "string" ? obj.pdfNotes : "",
    },
  };
}

export function validateTemplateDeleteInput(
  input: unknown,
): { ok: true; data: QuoteTemplateDeleteInput } | { ok: false; error: string } {
  if (!input || typeof input !== "object") {
    return { ok: false, error: "expectedUpdatedAt is required." };
  }
  const obj = input as Record<string, unknown>;
  if (typeof obj.expectedUpdatedAt !== "string" || !obj.expectedUpdatedAt) {
    return { ok: false, error: "expectedUpdatedAt is required." };
  }
  return { ok: true, data: { expectedUpdatedAt: obj.expectedUpdatedAt } };
}

export function validateTemplateUpdateInput(
  input: unknown,
): { ok: true; data: QuoteTemplateUpdateInput } | { ok: false; error: string } {
  if (!input || typeof input !== "object") {
    return { ok: false, error: "Invalid request body." };
  }
  const obj = input as Record<string, unknown>;
  if (typeof obj.expectedUpdatedAt !== "string" || !obj.expectedUpdatedAt) {
    return { ok: false, error: "expectedUpdatedAt is required." };
  }
  if (
    obj.name !== undefined &&
    (typeof obj.name !== "string" || !obj.name.trim())
  ) {
    return { ok: false, error: "Template name cannot be empty." };
  }
  if (
    obj.term !== undefined &&
    !["month_to_month", 12, 24, 36, 60].includes(obj.term as ContractTerm)
  ) {
    return {
      ok: false,
      error: "Valid contract term is required (month_to_month, 12, 24, 36, 60).",
    };
  }
  if (obj.lines !== undefined) {
    if (!Array.isArray(obj.lines)) {
      return { ok: false, error: "Lines must be an array." };
    }
    for (const line of obj.lines) {
      if (!line || typeof line !== "object") {
        return {
          ok: false,
          error: "Each line must be an object with productId and qty.",
        };
      }
      const l = line as Record<string, unknown>;
      if (typeof l.productId !== "string" || !l.productId) {
        return { ok: false, error: "Each line requires a productId." };
      }
      if (typeof l.qty !== "number" || l.qty <= 0) {
        return { ok: false, error: "Line quantity must be greater than zero." };
      }
    }
  }
  return {
    ok: true,
    data: {
      dealerId: typeof obj.dealerId === "string" ? obj.dealerId : undefined,
      expectedUpdatedAt: obj.expectedUpdatedAt,
      name: typeof obj.name === "string" ? obj.name.trim() : undefined,
      term: obj.term as ContractTerm | undefined,
      lines: Array.isArray(obj.lines)
        ? obj.lines.map((l: { productId: string; qty: number }) => ({
            productId: l.productId,
            qty: l.qty,
          }))
        : undefined,
      pdfNotes: typeof obj.pdfNotes === "string" ? obj.pdfNotes : undefined,
    },
  };
}
